import { Logger } from 'bernie-logger';

import { STORE_EVENTS } from 'stores/events/store-events';

import {
  DestinationPageLink,
  GalleryViewModel,
  HotelDetailsViewModel,
  HotelRoomsAndRatesViewModel,
  LocationSummaryViewModel,
  LocationViewModel,
  WhatsAroundModuleViewModel,
  WhatsAroundTransportation
} from 'models';

export const adaptPropertyInfoDetails = (response, useBexApiPdpMigrationFeatures, logger: Logger): HotelDetailsViewModel => {
  const propertyInfo = response?.data?.propertyInfo;
  const propertyInfoDetails = propertyInfo?.pwaOnlyBffResponse?.details;
  const hotel = propertyInfoDetails ? JSON.parse(propertyInfoDetails) : response;
  if (propertyInfo && useBexApiPdpMigrationFeatures) {
    hotel.gallery = adaptGallery(propertyInfo.propertyGallery) ?? logMissingFieldAndFallback(hotel.gallery, 'gallery', logger);

    const hotelName = propertyInfo.summary?.name ?? logMissingFieldAndFallback(hotel.hotelName, 'summary.name', logger);
    hotel.hotelName = hotelName;
    hotel.name = hotelName;

    if (hasWhatsAroundModuleData(response)) {
      const bffMapUrl = hotel.locationInfo?.whatsAroundModule?.mapImageUrl;
      hotel.locationInfo.whatsAroundModule = adaptWhatsAround(response);
      hotel.locationInfo.whatsAroundModule.mapImageUrl = bffMapUrl;
    } else {
      logMissingField('summary.location.whatsAround', logger);
    }

    if (hasNearbyPOIs(response)) {
      hotel.locationInfo.locationSummary = adaptLocationSummary(response);
    } else {
      logMissingField('summary.nearbyPOIs', logger);
    }

    hotel.starRatingIcon =
      propertyInfo.summary?.starRatingIcon?.id ?? logMissingFieldAndFallback(hotel.starRatingIcon, 'summary.starRatingIcon', logger);

    hotel.teleSalesNumber =
      propertyInfo.summary?.telesalesPhoneNumber ??
      logMissingFieldAndFallback(hotel.teleSalesNumber, 'summary.telesalesPhoneNumber', logger);

    hotel.stars =
      propertyInfo.summary?.overview?.starRating ?? logMissingFieldAndFallback(hotel.stars, 'summary.overview.starRating', logger);

    hotel.vipMessage =
      propertyInfo.summary?.overview?.vipMessaging ?? logMissingFieldAndFallback(hotel.vipMessage, 'summary.overview.vipMessaging', logger);
  }

  return hotel;
};

export const adaptPropertyInfoOffers = (response): HotelRoomsAndRatesViewModel => {
  const propertyInfoOffers = response?.data?.propertyInfo?.pwaOnlyBffResponse?.offers;

  return propertyInfoOffers ? JSON.parse(propertyInfoOffers) : response;
};

function hasNearbyPOIs(response: any) {
  return response?.data?.propertyInfo?.summary?.nearbyPOIs;
}

export const adaptGallery = (propertyGallery): GalleryViewModel => {
  if (!propertyGallery) {
    return propertyGallery;
  }
  const images = propertyGallery.images;
  const categories = propertyGallery.categories;

  return {
    images: images && (images as any[]).map(mapImages),
    categories:
      categories &&
      (categories as any[]).map(propertyGalleryCategory => ({
        id: propertyGalleryCategory.id,
        name: propertyGalleryCategory.name,
        images: propertyGalleryCategory.images && (propertyGalleryCategory.images as any[]).map(mapImages)
      }))
  };
};

const mapImages = (propertyImage: any) =>
  propertyImage && {
    alt: propertyImage.alt,
    caption: propertyImage.image?.description,
    src: propertyImage.image?.url,
    thumbnailSrc: propertyImage.thumbnail?.url,
    fallBackImage: propertyImage.fallbackImage?.url,
    subjectId: propertyImage.subjectId
  };

function logMissingFieldAndFallback(fallbackValue, fieldName, logger) {
  logMissingField(fieldName, logger);

  return fallbackValue;
}

function logMissingField(fieldName, logger) {
  logger.logEvent(STORE_EVENTS.BEX_API_PDP_MIGRATION_MISSING_FIELD, `${fieldName} is missing from propertyInfo`);
}

export function hasWhatsAroundModuleData(response: any) {
  return response.data?.propertyInfo?.summary?.location?.whatsAround;
}

export function adaptWhatsAround(response: any): WhatsAroundModuleViewModel {
  const whatsAround = response.data.propertyInfo.summary.location.whatsAround;
  const nearbyPOIs = whatsAround.nearbyPOIs.find(item => item.icon.id === 'place');
  const transportation = whatsAround.nearbyPOIs.find(item => item.icon.id === 'train');
  const restaurants = whatsAround.nearbyPOIs.find(item => item.icon.id === 'local_dining');

  return {
    destinationPageLink: whatsAround.editorial.crossLink ? adaptLodgingLinkMessage(whatsAround.editorial.crossLink) : null,
    locationEditorial: whatsAround.editorial.content[0],
    nearbyPOIs: nearbyPOIs ? adaptNearbyPOIs(nearbyPOIs.items) : [],
    nearbyTransportation: transportation ? adaptNearbyTransportation(transportation.items) : [],
    neighborhood: whatsAround.editorial.title,
    restaurants: restaurants ? restaurants.items.map(item => item.text) : [],
    subtitlePOIs: nearbyPOIs ? nearbyPOIs.title : '',
    subtitleRestaurants: restaurants ? restaurants.title : '',
    subtitleTransportations: transportation ? transportation.title : '',
    title: whatsAround.title
  } as WhatsAroundModuleViewModel;
}

export function adaptLodgingLinkMessage(lodgingLinkMessage): DestinationPageLink {
  return {
    text: lodgingLinkMessage.value,
    url: lodgingLinkMessage.link.url
  } as DestinationPageLink;
}

export function adaptNearbyPOIs(propertyInfoItems): LocationViewModel[] {
  return propertyInfoItems.map(
    item =>
      ({
        name: item.text,
        distance: item.moreInfo,
        category: item.icon.id,
        accessibleCategory: item.icon.description
      } as LocationViewModel)
  );
}

export function adaptNearbyTransportation(propertyInfoItems): WhatsAroundTransportation[] {
  return propertyInfoItems.map(
    item =>
      ({
        category: item.icon.id,
        content: item.text
      } as WhatsAroundTransportation)
  );
}

export function adaptLocationSummary(response: any): LocationSummaryViewModel {
  const nearbyPOIs = response.data.propertyInfo.summary.nearbyPOIs;

  return {
    title: nearbyPOIs.title,
    locations: adaptNearbyPOIs(nearbyPOIs.items)
  } as LocationSummaryViewModel;
}
